import type { CalendarDate } from '@internationalized/date';
import type { AriaCalendarProps, DateValue } from 'react-aria';
import type { CalendarState } from 'react-stately';

import { createCalendar, getWeeksInMonth, isToday } from '@internationalized/date';
import { useRef } from 'react';
import { useButton, useCalendar, useCalendarCell, useCalendarGrid, useLocale } from 'react-aria';
import { useCalendarState } from 'react-stately';

import { clsx } from '~/styles/utils';

import { Icon } from '../../Icon/Icon';
import {
  calendarStyle,
  cellStyle,
  dayDisabledStyle,
  dayOutsideStyle,
  daySelectedStyle,
  dayStyle,
  dayTodayStyle,
  gridStyle,
  headerStyle,
  headingStyle,
  navButtonStyle,
  weekdayStyle,
} from './Calendar.css';

type Props = AriaCalendarProps<DateValue>;

export const Calendar = (delegated: Props) => {
  const { locale } = useLocale();
  const state = useCalendarState({ ...delegated, createCalendar, locale });
  const { calendarProps, nextButtonProps, prevButtonProps, title } = useCalendar(delegated, state);

  return (
    <div {...calendarProps} className={calendarStyle}>
      <div className={headerStyle}>
        <NavButton {...prevButtonProps} icon="ChevronBackward" />
        <h2 className={headingStyle}>{title}</h2>
        <NavButton {...nextButtonProps} icon="ChevronForward" />
      </div>

      <MonthGrid locale={locale} state={state} />
    </div>
  );
};

type NavButtonProps = ReturnType<typeof useCalendar>['prevButtonProps'] & { icon: 'ChevronBackward' | 'ChevronForward' };

const NavButton = ({ icon, ...delegated }: NavButtonProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const { buttonProps } = useButton(delegated, ref);

  return (
    <button {...buttonProps} className={navButtonStyle} ref={ref} type="button">
      <Icon name={icon} size={20} />
    </button>
  );
};

const MonthGrid = ({ locale, state }: { locale: string; state: CalendarState }) => {
  const { gridProps, headerProps, weekDays } = useCalendarGrid({ weekdayStyle: 'short' }, state);
  const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);

  return (
    <table {...gridProps} className={gridStyle}>
      <thead {...headerProps}>
        <tr>
          {weekDays.map((day, index) => (
            <th className={weekdayStyle} key={index}>
              {day}
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
          <tr key={weekIndex}>
            {state
              .getDatesInWeek(weekIndex)
              .map((date, dayIndex) => (date ? <Day date={date} key={dayIndex} state={state} /> : <td key={dayIndex} />))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const Day = ({ date, state }: { date: CalendarDate; state: CalendarState }) => {
  const ref = useRef<HTMLButtonElement>(null);
  const { buttonProps, formattedDate, isDisabled, isOutsideVisibleRange, isSelected } = useCalendarCell({ date }, state, ref);

  return (
    <td className={cellStyle}>
      <button
        {...buttonProps}
        className={clsx(
          dayStyle,
          isToday(date, state.timeZone) && dayTodayStyle,
          isOutsideVisibleRange && dayOutsideStyle,
          isSelected && daySelectedStyle,
          isDisabled && dayDisabledStyle,
        )}
        ref={ref}
        type="button"
      >
        {formattedDate}
      </button>
    </td>
  );
};
